import styled from 'styled-components';

import { StyledDefaultTableBodyRow } from '../../../../../cdl/TableBuilder/components/StyledDefaultTableBodyRow';
import { Text } from '../../../../../cdl/Text/Text';
import { Tooltip } from '../../../../../cdl/Tooltip/Tooltip';
import { formatNumber } from '../../../../../common/helpers/formatNumber.helper';
import { translate } from '../../../../../common/helpers/translate.helper';
import { Characters } from '../../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../../common/hooks/useEnums';
import { theme } from '../../../../../common/ui/theme';
import { LINE_ITEM_COMPARISON_PRODUCT_COLUMN_MAX_WIDTH } from '../lineItemComparisonDimensions';
import { PriceCellContent } from '../PriceCellContent';
import { stickyProductColumnStyles } from '../stickyProductColumnStyles';
import type { LineItemComparisonRow as LineItemComparisonRowModel } from '../types/LineItemComparisonRow';
import { LineItemComparisonRowType } from '../types/LineItemComparisonRowType';
import { useIsTruncated } from '../useIsTruncated';

const ProductDetails = styled.div`
    display: grid;
    row-gap: ${theme.space[2]};
    width: max-content;
    max-width: calc(${LINE_ITEM_COMPARISON_PRODUCT_COLUMN_MAX_WIDTH}px - 2 * ${theme.space[16]});
`;

const TruncatedText = styled(Text)`
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const BodyCell = styled.td`
    border-top: 1px solid ${theme.colors.background.inset};
    padding: ${theme.space[12]} ${theme.space[16]};
    text-align: right;
    vertical-align: top;
`;

const ProductCell = styled(BodyCell)<{ $showScrollShadow: boolean }>`
    ${stickyProductColumnStyles};
    z-index: 1;
    background-color: ${theme.colors.background.default};
    max-width: ${LINE_ITEM_COMPARISON_PRODUCT_COLUMN_MAX_WIDTH}px;
    text-align: left;

    tr:nth-child(odd) & {
        background-color: ${theme.colors.background.subtle};
    }
`;

const PriceCell = styled(BodyCell)`
    padding-right: calc(${theme.space[16]} + ${theme.space[6]});
`;

const getProductSubLabel = (
    row: LineItemComparisonRowModel,
    getHumanReadableValue: ReturnType<typeof useEnums>['getHumanReadableValue']
) => {
    const productGroup = row.productGroup ? getHumanReadableValue('productGroup', row.productGroup) : null;
    const volume =
        row.volume !== undefined && row.unit
            ? `${formatNumber({
                  number: row.volume,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
              })}${Characters.NO_BREAK_SPACE}${getHumanReadableValue('productUnit', row.unit)}`
            : null;

    return [productGroup, volume]
        .filter(Boolean)
        .join(`${Characters.EN_SPACE}${Characters.MIDDLE_DOT}${Characters.EN_SPACE}`);
};

interface LineItemComparisonRowProps {
    row: LineItemComparisonRowModel;
    showScrollShadow: boolean;
}

export const LineItemComparisonRow = ({ row, showScrollShadow }: LineItemComparisonRowProps) => {
    const { getHumanReadableValue } = useEnums();
    const { ref: productNameRef, isTruncated: isProductNameTruncated } = useIsTruncated<HTMLSpanElement>(row.label);
    const productName = (
        <TruncatedText ref={productNameRef} fontWeight={600}>
            {row.label}
        </TruncatedText>
    );

    return (
        <StyledDefaultTableBodyRow active={false} selectable={false} alternateRowColors grow>
            <ProductCell $showScrollShadow={showScrollShadow}>
                <ProductDetails>
                    {isProductNameTruncated ? (
                        <Tooltip label={row.label} side="top" align="center" showArrow>
                            {productName}
                        </Tooltip>
                    ) : (
                        productName
                    )}
                    {row.type === LineItemComparisonRowType.SAMPLE_KIT && row.subLabel ? (
                        <TruncatedText variant="extraSmall" color="foreground.subtle">
                            {translate('order.detail.lineItemComparison.sampleKitQuantity', {
                                quantity: row.subLabel,
                            })}
                        </TruncatedText>
                    ) : null}
                    {row.type === LineItemComparisonRowType.PRODUCT ? (
                        <TruncatedText variant="extraSmall" color="foreground.subtle">
                            {getProductSubLabel(row, getHumanReadableValue)}
                        </TruncatedText>
                    ) : null}
                </ProductDetails>
            </ProductCell>
            {row.cells.map((cell) => (
                <PriceCell key={cell.offerId}>
                    <PriceCellContent cell={cell} row={row} />
                </PriceCell>
            ))}
        </StyledDefaultTableBodyRow>
    );
};
